import {
	type ProductImportViolation,
	ProductImportViolationViolationTypeEnum,
	type TemplateImportViolation,
	TemplateImportViolationViolationTypeEnum,
} from '@api-client'
import { CloseIcon, DownloadIcon } from '@assets/icons'
import {
	Box,
	Button,
	CircularProgress,
	IconButton,
	Link,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableRow,
	Typography,
	useTheme,
} from '@mui/material'
import { isAxiosError } from 'axios'
import { useSnackbar } from 'notistack'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import * as S from './styles'

interface IProps {
	isFileValid?: boolean
	isLoading?: boolean
	templateUrl?: string
	acceptsPactJson?: boolean
	violations?: readonly (ProductImportViolation | TemplateImportViolation)[]
	onChangeFile: (file?: File) => Promise<void>
	selectedFile?: File
}

interface ViolationsType {
	invalidHeader?: boolean
	missingProductName?: boolean
	invalidFileFormat?: boolean
	invalidJsonFormat?: string
	invalidPactFormat?: boolean
	invalidNumberOfCells?: readonly number[]
	missingRefNum?: boolean
	organisationOrEmailNotFound?: readonly string[]
	invalidValue?: readonly string[]
	duplicateRefNumInFile?: {
		value: boolean
		duplicates?: readonly string[]
		exceedsDuplicatesCount?: boolean
	}
	duplicateRefNumInSystem?: {
		value: boolean
		duplicates?: readonly string[]
		exceedsDuplicatesCount?: boolean
	}
}

const mapViolationsToState = (
	violations?: readonly (ProductImportViolation | TemplateImportViolation)[]
) =>
	// oxlint-disable-next-line no-array-reduce
	violations?.reduce(
		(
			prevState: ViolationsType,
			err: ProductImportViolation | TemplateImportViolation
		): ViolationsType => {
			switch (err.violationType) {
				case ProductImportViolationViolationTypeEnum.INVALID_HEADER:
					return { ...prevState, invalidHeader: true }
				case ProductImportViolationViolationTypeEnum.INVALID_FILE_FORMAT:
					return {
						...prevState,
						invalidFileFormat: true,
					}
				case ProductImportViolationViolationTypeEnum.MISSING_PRODUCT_NAME:
					return {
						...prevState,
						missingProductName: true,
					}
				case ProductImportViolationViolationTypeEnum.MISSING_PRODUCT_REFERENCE_NUMBER:
					return { ...prevState, missingRefNum: true }
				case ProductImportViolationViolationTypeEnum.INVALID_NUMBER_OF_CELLS:
					return {
						...prevState,
						invalidNumberOfCells: [
							...(prevState.invalidNumberOfCells || []),
							err.lineNumber,
						],
					}
				case ProductImportViolationViolationTypeEnum.DUPLICATED_REFERENCE_IN_FILE: {
					const duplicatedReferencesInFile =
						prevState.duplicateRefNumInFile?.duplicates
					if ((duplicatedReferencesInFile?.length ?? 0) >= 5) {
						return {
							...prevState,
							duplicateRefNumInFile: {
								value: true,
								exceedsDuplicatesCount: true,
							},
						}
					}
					return {
						...prevState,
						duplicateRefNumInFile: {
							value: true,
							duplicates: [
								...(duplicatedReferencesInFile || []),
								err.reference,
							],
						},
					}
				}
				case ProductImportViolationViolationTypeEnum.DUPLICATED_REFERENCE_IN_SYSTEM: {
					const duplicatedReferencesInSystem =
						prevState.duplicateRefNumInSystem?.duplicates
					if ((duplicatedReferencesInSystem?.length ?? 0) >= 5) {
						return {
							...prevState,
							duplicateRefNumInSystem: {
								value: true,
								exceedsDuplicatesCount: true,
							},
						}
					}
					return {
						...prevState,
						duplicateRefNumInSystem: {
							value: true,
							duplicates: [
								...(duplicatedReferencesInSystem || []),
								err.reference,
							],
						},
					}
				}
				case ProductImportViolationViolationTypeEnum.ORGANISATION_OR_EMAIL_NOT_FOUND:
					return {
						...prevState,
						organisationOrEmailNotFound: [
							...(prevState.organisationOrEmailNotFound || []),
							err.reference,
						],
					}
				case ProductImportViolationViolationTypeEnum.INVALID_VALUE:
					return {
						...prevState,
						invalidValue: [...(prevState.invalidValue || []), err.reference],
					}
				case TemplateImportViolationViolationTypeEnum.INVALID_JSON_FORMAT:
					return { ...prevState, invalidJsonFormat: err.reference }
				case TemplateImportViolationViolationTypeEnum.INVALID_PACT_FORMAT:
					return { ...prevState, invalidPactFormat: true }
				case TemplateImportViolationViolationTypeEnum.DUPLICATED_REFERENCE_IN_FILE: {
					const duplicatedReferencesInFile =
						prevState.duplicateRefNumInFile?.duplicates
					if ((duplicatedReferencesInFile?.length ?? 0) >= 5) {
						return {
							...prevState,
							duplicateRefNumInFile: {
								value: true,
								exceedsDuplicatesCount: true,
							},
						}
					}
					return {
						...prevState,
						duplicateRefNumInFile: {
							value: true,
							duplicates: [
								...(duplicatedReferencesInFile || []),
								err.reference,
							],
						},
					}
				}
				case TemplateImportViolationViolationTypeEnum.DUPLICATED_REFERENCE_IN_SYSTEM: {
					const duplicatedReferencesInSystem =
						prevState.duplicateRefNumInSystem?.duplicates
					if ((duplicatedReferencesInSystem?.length ?? 0) >= 5) {
						return {
							...prevState,
							duplicateRefNumInSystem: {
								value: true,
								exceedsDuplicatesCount: true,
							},
						}
					}
					return {
						...prevState,
						duplicateRefNumInSystem: {
							value: true,
							duplicates: [
								...(duplicatedReferencesInSystem || []),
								err.reference,
							],
						},
					}
				}
			}
			return prevState
		},
		{}
	)

const UploadProducts = (props: IProps) => {
	const { t } = useTranslation()
	const { palette } = useTheme()
	const { enqueueSnackbar } = useSnackbar()
	const {
		templateUrl: productsTemplateUrl = '/files/products_template.xlsx',
		acceptsPactJson = false,
		onChangeFile,
		selectedFile,
		isFileValid,
		isLoading: forceIsLoading,
		violations,
	} = props
	const violationsCount = violations?.length || 0
	const allWarnings = violations?.filter(file => file.isError).length === 0
	const isWarning = !!violations && allWarnings
	const isError = !!violations && !allWarnings
	const blockersCount =
		violations?.filter(
			item =>
				item.violationType !==
					ProductImportViolationViolationTypeEnum.DUPLICATED_REFERENCE_IN_SYSTEM &&
				item.violationType !==
					ProductImportViolationViolationTypeEnum.DUPLICATED_REFERENCE_IN_FILE
		).length || 0
	const errorState = useMemo(
		() => mapViolationsToState(violations) || {},
		[violations]
	)
	const [isLoading, setIsLoading] = useState<boolean>(false)
	const inputFile = useRef(null)

	const onUploadFilesChange = useCallback(
		async (e: React.ChangeEvent<HTMLInputElement>) => {
			if (!e.target.files) {
				return
			}
			setIsLoading(true)
			try {
				await onChangeFile?.(e.target.files[0])
			} catch (e) {
				if (isAxiosError(e)) {
					const errResponse = e.response
					if (errResponse?.status === 500) {
						enqueueSnackbar(t('toast.error.general'), {
							variant: 'error',
						})
					}
				}
			} finally {
				setIsLoading(false)
			}
		},
		[enqueueSnackbar, onChangeFile, t]
	)

	const deleteFile = useCallback(() => onChangeFile?.(), [onChangeFile])

	const handleInputFileClick = useCallback(() => {
		;(inputFile?.current as unknown as HTMLInputElement | null)?.click()
	}, [])

	return (
		<Stack width={760} gap={2}>
			<Stack>
				<Typography variant="h2">
					{acceptsPactJson
						? t('productModal.bulk.titleWithPact')
						: t('productModal.bulk.title')}
				</Typography>
				<Typography variant="subtitle2" component="h5" color="textSecondary">
					{t('productModal.bulk.subtitle')}
				</Typography>
			</Stack>
			<Box display="flex" justifyContent="space-between" />
			<Box display="flex" gap={1}>
				<Button
					variant="outlined"
					disabled={isLoading || forceIsLoading}
					onClick={handleInputFileClick}
				>
					{' '}
					<input
						type="file"
						accept={
							acceptsPactJson
								? '.csv, .json, .zip, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, application/json, application/zip'
								: '.csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel'
						}
						hidden
						onChange={onUploadFilesChange}
						ref={inputFile}
					/>
					{(isLoading || forceIsLoading) && (
						<CircularProgress size={24} sx={{ marginRight: 1 }} />
					)}
					{acceptsPactJson
						? t('productModal.bulk.importCsvExcelOrPactJson')
						: t('productModal.bulk.importCsvOrExcel')}
				</Button>
				<Link href={productsTemplateUrl} download target="_self">
					<Button variant="text" sx={{ textTransform: 'none' }}>
						<DownloadIcon style={{ marginRight: '10px' }} />
						{t('productModal.bulk.downloadOurModel')}
					</Button>
				</Link>
			</Box>

			{!isLoading && selectedFile && (
				<>
					<S.FileUploadedBox>
						<Stack>
							<Typography variant="subtitle1">{selectedFile?.name}</Typography>
							<Typography variant="body2">
								{t('byteSize', { size: selectedFile?.size })}
							</Typography>
						</Stack>
						<IconButton onClick={deleteFile}>
							<CloseIcon />
						</IconButton>
					</S.FileUploadedBox>
					{isFileValid && (
						<Typography color="textSecondary">
							{t('productModal.bulk.validFile')}
						</Typography>
					)}
				</>
			)}

			{!isLoading && (isError || isWarning) && (
				<S.FileUploadErrorBox>
					<Stack>
						{errorState.invalidHeader && (
							<>
								<Typography variant="subtitle2" gutterBottom>
									{t('files.uploadErrors.incorrectHeader')}
								</Typography>
								<Typography variant="body2" gutterBottom>
									{t('files.uploadErrors.pleaseCheckHeader')}
								</Typography>
							</>
						)}
						{errorState.invalidFileFormat && (
							<>
								<Typography variant="subtitle2" gutterBottom>
									{t('files.uploadErrors.invalidFileFormat')}
								</Typography>
								<Typography variant="body2" gutterBottom>
									{t('files.uploadErrors.pleaseCheckFileFormat')}
								</Typography>
							</>
						)}
						{errorState.invalidJsonFormat && (
							<>
								<Typography variant="subtitle2" gutterBottom>
									{t('files.uploadErrors.invalidJsonFormat')}
								</Typography>
								<Typography variant="body2" gutterBottom>
									{t('files.uploadErrors.pleaseCheckPactJsonFormat')}
								</Typography>
								<Typography
									variant="body2"
									gutterBottom
									sx={{ fontFamily: 'monospace', wordBreak: 'break-word' }}
								>
									{errorState.invalidJsonFormat}
								</Typography>
							</>
						)}
						{errorState.invalidPactFormat && (
							<>
								<Typography variant="subtitle2" gutterBottom>
									{t('files.uploadErrors.invalidPactFormat')}
								</Typography>
								<Typography variant="body2" gutterBottom>
									{t('files.uploadErrors.pleaseCheckPactFields')}
								</Typography>
							</>
						)}
						{!errorState.invalidHeader &&
							!errorState.invalidFileFormat &&
							!errorState.invalidJsonFormat &&
							!errorState.invalidPactFormat &&
							!isWarning && (
								<Typography variant="subtitle2" gutterBottom>
									{t('files.uploadErrors.issuesWithBlockers', {
										violationsCount,
										blockersCount,
									})}
								</Typography>
							)}
						<Table>
							<TableBody>
								{errorState.missingProductName && (
									<TableRow>
										<TableCell
											sx={{ color: palette.error.main, fontWeight: 'bold' }}
										>
											{t('files.uploadErrors.blocker')}
										</TableCell>
										<TableCell>{t('table.productName')}</TableCell>
										<TableCell>
											{t('files.uploadErrors.missingField')}
										</TableCell>
										<TableCell>
											{t('files.uploadErrors.missingProductName')}
										</TableCell>
									</TableRow>
								)}
								{errorState.missingRefNum && (
									<TableRow>
										<TableCell
											sx={{ color: palette.error.main, fontWeight: 'bold' }}
										>
											{t('files.uploadErrors.blocker')}
										</TableCell>
										<TableCell>{t('table.referenceNumber')}</TableCell>
										<TableCell>
											{t('files.uploadErrors.missingField')}
										</TableCell>
										<TableCell>
											{t('files.uploadErrors.missingReferenceNum')}
										</TableCell>
									</TableRow>
								)}
								{errorState.invalidNumberOfCells?.length ? (
									<TableRow>
										<TableCell
											sx={{ color: palette.error.main, fontWeight: 'bold' }}
										>
											{t('files.uploadErrors.blocker')}
										</TableCell>
										<TableCell>
											{t('files.uploadErrors.invalidNumberOfCells.tableHeader')}
										</TableCell>
										<TableCell>
											{t('files.uploadErrors.invalidNumberOfCells.description')}
										</TableCell>
										<TableCell>
											{t('files.uploadErrors.invalidNumberOfCells.details', {
												count: errorState.invalidNumberOfCells.length,
												lineNumber: errorState.invalidNumberOfCells[0],
											})}
										</TableCell>
									</TableRow>
								) : undefined}
								{errorState.organisationOrEmailNotFound?.map((reference, i) => (
									<TableRow key={i}>
										<TableCell
											sx={{ color: palette.error.main, fontWeight: 'bold' }}
										>
											{t('files.uploadErrors.blocker')}
										</TableCell>
										<TableCell>{t('table.supplier')}</TableCell>
										<TableCell>
											{t('files.uploadErrors.organisationOrEmailNotFound')}
										</TableCell>
										<TableCell>{reference}</TableCell>
									</TableRow>
								))}
								{errorState.invalidValue?.map((reference, i) => (
									<TableRow key={i}>
										<TableCell
											sx={{ color: palette.error.main, fontWeight: 'bold' }}
										>
											{t('files.uploadErrors.blocker')}
										</TableCell>
										<TableCell>
											{t('files.uploadErrors.invalidValue.tableHeader')}
										</TableCell>
										<TableCell>
											{t('files.uploadErrors.invalidValue.description')}
										</TableCell>
										<TableCell>{reference}</TableCell>
									</TableRow>
								))}
								{errorState.duplicateRefNumInFile?.value &&
								errorState.duplicateRefNumInFile.exceedsDuplicatesCount ? (
									<TableRow>
										<TableCell
											sx={{ color: palette.fonts.warning, fontWeight: 'bold' }}
										>
											{t('warning')}
										</TableCell>
										<TableCell>{t('table.referenceNumber')}</TableCell>
										<TableCell>
											{t(
												'files.uploadWarnings.duplicateReferenceNumberInSystem'
											)}
										</TableCell>
										<TableCell>
											{t('files.uploadWarnings.tooManyDuplicatesInFile')}
										</TableCell>
									</TableRow>
								) : (
									errorState.duplicateRefNumInFile?.duplicates?.map(
										(item, i) => (
											<TableRow key={i}>
												<TableCell
													sx={{
														color: palette.fonts.warning,
														fontWeight: 'bold',
													}}
												>
													{t('warning')}
												</TableCell>
												<TableCell>{t('table.referenceNumber')}</TableCell>
												<TableCell>
													{t('files.uploadWarnings.duplicateReferenceNumber')}
												</TableCell>
												<TableCell>
													{t(
														'files.uploadWarnings.duplicateReferenceNumberInFile',
														{ reference: item }
													)}
												</TableCell>
											</TableRow>
										)
									)
								)}
								{errorState.duplicateRefNumInSystem?.value &&
								errorState.duplicateRefNumInSystem.exceedsDuplicatesCount ? (
									<TableRow>
										<TableCell
											sx={{ color: palette.fonts.warning, fontWeight: 'bold' }}
										>
											{t('warning')}
										</TableCell>
										<TableCell>{t('table.referenceNumber')}</TableCell>
										<TableCell>
											{t(
												'files.uploadWarnings.duplicateReferenceNumberInSystem'
											)}
										</TableCell>
										<TableCell>
											{t('files.uploadWarnings.tooManyDuplicatesInSystem')}
										</TableCell>
									</TableRow>
								) : (
									errorState.duplicateRefNumInSystem?.duplicates?.map(
										(item, i) => (
											<TableRow key={i}>
												<TableCell
													sx={{
														color: palette.fonts.warning,
														fontWeight: 'bold',
													}}
												>
													{t('warning')}
												</TableCell>
												<TableCell>{t('table.referenceNumber')}</TableCell>
												<TableCell>
													{t(
														'files.uploadWarnings.duplicateReferenceNumberInSystem'
													)}
												</TableCell>
												<TableCell>
													{t('files.uploadWarnings.referenceAlreadyAdded', {
														reference: item,
													})}
												</TableCell>
											</TableRow>
										)
									)
								)}
							</TableBody>
						</Table>
					</Stack>
				</S.FileUploadErrorBox>
			)}
		</Stack>
	)
}

export { UploadProducts }
